import {
  Text,
  Card,
  Checkbox,
  Field,
  Dropdown,
  Option,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  LaptopRegular,
  DesktopRegular,
  TabletRegular,
  PhoneRegular,
  PlugConnectedRegular,
  KeyboardRegular,
  HeadphonesRegular,
  PrintRegular,
  ScanRegular,
  CameraRegular,
  CursorRegular,
} from '@fluentui/react-icons';
import { MAIN_HARDWARE, PERIPHERALS, MONITOR_QUANTITIES } from '../../../config/constants';
import { HardwareRequestFormData } from '../../../hooks/useHardwareRequestForm';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  hardwareCard: {
    padding: tokens.spacingVerticalM,
    cursor: 'pointer',
    borderTopWidth: '2px',
    borderRightWidth: '2px',
    borderBottomWidth: '2px',
    borderLeftWidth: '2px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    ':hover': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  hardwareCardSelected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorBrandBackground2,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    textAlign: 'center',
  },
  icon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  dropdownField: {
    maxWidth: '300px',
  },
  textareaField: {
    maxWidth: '700px',
  },
  charCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

interface HardwareSelectionStepProps {
  formData: HardwareRequestFormData;
  toggleMainHardware: (item: string) => void;
  togglePeripheral: (item: string) => void;
  setMonitorQuantity: (quantity: number) => void;
  setJustification: (text: string) => void;
}

export function HardwareSelectionStep({
  formData,
  toggleMainHardware,
  togglePeripheral,
  setMonitorQuantity,
  setJustification,
}: HardwareSelectionStepProps) {
  const styles = useStyles();

  // Icon mapping for hardware items
  const getHardwareIcon = (item: string) => {
    switch (item) {
      case 'Laptop':
        return <LaptopRegular className={styles.icon} />;
      case 'Desktop':
        return <DesktopRegular className={styles.icon} />;
      case 'iPad':
        return <TabletRegular className={styles.icon} />;
      case 'Mobile Phone':
        return <PhoneRegular className={styles.icon} />;
      case 'Docking Station':
        return <PlugConnectedRegular className={styles.icon} />;
      case 'Mouse':
        return <CursorRegular className={styles.icon} />;
      case 'Keyboard':
        return <KeyboardRegular className={styles.icon} />;
      case 'Headset':
        return <HeadphonesRegular className={styles.icon} />;
      case 'Printer':
        return <PrintRegular className={styles.icon} />;
      case 'Scanner':
        return <ScanRegular className={styles.icon} />;
      case 'Camera':
        return <CameraRegular className={styles.icon} />;
      default:
        return null;
    }
  };

  const justificationLength = formData.justification.trim().length;
  const isJustificationValid = justificationLength >= 10;

  return (
    <div className={styles.container}>
      {/* Main Hardware Section */}
      <div className={styles.section}>
        <Text size={500} weight="semibold">
          Main Hardware
        </Text>
        <Text>Select one or more main hardware items (optional).</Text>
        <div className={styles.cardGrid}>
          {MAIN_HARDWARE.map((item) => {
            const isSelected = formData.mainHardware.includes(item);
            return (
              <Card
                key={item}
                className={`${styles.hardwareCard} ${isSelected ? styles.hardwareCardSelected : ''}`}
                onClick={() => toggleMainHardware(item)}
              >
                <div className={styles.cardContent}>
                  {getHardwareIcon(item)}
                  <Checkbox
                    checked={isSelected}
                    label={item}
                    onChange={() => toggleMainHardware(item)}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Peripherals Section */}
      <div className={styles.section}>
        <Text size={500} weight="semibold">
          Peripherals
        </Text>
        <Text>Select peripherals (optional).</Text>
        <div className={styles.cardGrid}>
          {PERIPHERALS.map((item) => {
            const isSelected = formData.peripherals.includes(item);
            return (
              <Card
                key={item}
                className={`${styles.hardwareCard} ${isSelected ? styles.hardwareCardSelected : ''}`}
                onClick={() => togglePeripheral(item)}
              >
                <div className={styles.cardContent}>
                  {getHardwareIcon(item)}
                  <Checkbox
                    checked={isSelected}
                    label={item}
                    onChange={() => togglePeripheral(item)}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Monitors Section */}
      <div className={styles.section}>
        <Text size={500} weight="semibold">
          Monitors
        </Text>
        <Field label="How many monitors?" className={styles.dropdownField}>
          <Dropdown
            value={MONITOR_QUANTITIES.find((q) => q.key === formData.monitorQuantity)?.text || 'No monitors'}
            selectedOptions={[String(formData.monitorQuantity)]}
            onOptionSelect={(_, data) => {
              setMonitorQuantity(Number(data.optionValue));
            }}
          >
            {MONITOR_QUANTITIES.map((quantity) => (
              <Option key={quantity.key} value={String(quantity.key)} text={quantity.text}>
                {quantity.text}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      {/* Justification Section */}
      <div className={styles.section}>
        <Text size={500} weight="semibold">
          Business Justification
        </Text>
        <Field
          label="Explain the business need for this hardware (required)"
          validationMessage={
            !isJustificationValid && justificationLength > 0
              ? 'Justification must be at least 10 characters'
              : undefined
          }
          validationState={
            !isJustificationValid && justificationLength > 0 ? 'error' : 'none'
          }
          className={styles.textareaField}
        >
          <Textarea
            placeholder="Example: Remote work setup for new project manager role, replacing old equipment, expanding team capacity..."
            value={formData.justification}
            onChange={(_, data) => setJustification(data.value)}
            rows={4}
          />
          <div className={justificationLength < 10 ? styles.errorText : styles.charCount}>
            {justificationLength} / 2000 characters (minimum 10)
          </div>
        </Field>
      </div>
    </div>
  );
}
